import { useCallback, useEffect, useMemo, useState } from 'react';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import {
  Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto,
  Pwerm2CalculationResultsDto,
  Pwerm2CalculationWaterfallCaseYearInstrumentDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { useLocale } from '@app/core/hooks/useLocale';

export enum CashFlowType {
  Infusion,
  Other,
}

export interface SelectedCellState {
  caseId: string;
  forecastId: string;
  instrument: InstrumentDefinitionWithId;
}

export const useTotalPvModal = (
  cashFlowType: CashFlowType,
  results: Pwerm2CalculationResultsDto,
  hasInfusions?: boolean,
  isOrdinaryEquity?: boolean
) => {
  const { l } = useLocale();
  const [tableTitle, setTableTitle] = useState<string | undefined>(undefined);
  const [exitDate, setExitDate] = useState<string | undefined>(undefined);
  const [totalPvValues, setTotalPvValues] = useState<{
    title: string | undefined;
    value: number | undefined;
  }>({ title: undefined, value: undefined });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [instrumentData, setInstrumentData] =
    useState<Pwerm2CalculationWaterfallCaseYearInstrumentDto | null>();
  const [transactions, setTransactions] = useState<
    Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[] | null
  >(null);

  // Precompute Maps for O(1) transaction type lookups with indices
  const transactionTypeMap = useMemo(() => {
    const map = new Map<
      Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto,
      { type: 'infusion' | 'redemption' | 'cashPaid' | 'dividend'; index: number }
    >();

    if (!instrumentData) {
      return map;
    }

    instrumentData.infusionTransactions?.forEach((transaction, index) => {
      map.set(transaction, { type: 'infusion', index });
    });

    instrumentData.redemptionTransactions?.forEach((transaction, index) => {
      map.set(transaction, { type: 'redemption', index });
    });

    instrumentData.cashPaidTransactions?.forEach((transaction, index) => {
      map.set(transaction, { type: 'cashPaid', index });
    });

    instrumentData.dividendTransactions?.forEach((transaction, index) => {
      map.set(transaction, { type: 'dividend', index });
    });

    return map;
  }, [instrumentData]);

  const getTransactionTypeLabel = (
    transaction: Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto
  ): string => {
    const transactionInfo = transactionTypeMap.get(transaction);

    if (!transactionInfo) {
      return '';
    }

    const { type, index } = transactionInfo;

    switch (type) {
      case 'infusion':
        return `${l('_Infusion')} ${index + 1}`;
      case 'redemption':
        return `${isOrdinaryEquity ? l('_Buyback') : l('_Redemption')} ${index + 1}`;
      case 'cashPaid':
        return `${l('_CashInterest')} ${index + 1}`;
      case 'dividend':
        return `${l('_Dividend')} ${index + 1}`;
      default:
        return '';
    }
  };

  const isTransactionCashPaidOrDividend = (
    transaction: Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto
  ): boolean => {
    const transactionInfo = transactionTypeMap.get(transaction);
    return transactionInfo?.type === 'cashPaid' || transactionInfo?.type === 'dividend';
  };

  const getYearForecast = (caseId: string, forecastId: string) =>
    results?.waterfall?.cases?.[caseId]?.years?.[forecastId];

  const handleCellClick = (
    caseId: string,
    forecastId: string,
    instrument: InstrumentDefinitionWithId
  ) => {
    const yearForecast = getYearForecast(caseId, forecastId);
    setInstrumentData(yearForecast.instruments[instrument.instrumentId]);
    setExitDate(yearForecast.exitDate);
    setIsOpen(true);
  };

  const getTransactions = useCallback(
    (instrumentData?: Pwerm2CalculationWaterfallCaseYearInstrumentDto | null | undefined) => {
      switch (cashFlowType) {
        case CashFlowType.Infusion:
          return instrumentData?.infusionTransactions ?? [];
        case CashFlowType.Other:
          return [
            ...(instrumentData?.redemptionTransactions ?? []),
            ...(instrumentData?.cashPaidTransactions ?? []),
            ...(instrumentData?.dividendTransactions ?? []),
          ];
      }
    },
    [cashFlowType]
  );

  const getTotalPvValues = useCallback(() => {
    switch (cashFlowType) {
      case CashFlowType.Infusion:
        return {
          title: l('_TotalPvOfInfusions'),
          value: instrumentData?.transactionSummary.totalPvOfInfusionMovement,
        };
      case CashFlowType.Other:
        return {
          title: hasInfusions
            ? l('_TotalPvOfOtherInterimCashFlows')
            : l('_TotalPvOfInterimCashFlows'),
          value: instrumentData?.transactionSummary.totalPvOfOtherMovement,
        };
    }
  }, [cashFlowType, instrumentData, l, hasInfusions]);

  useEffect(() => {
    if (instrumentData) {
      setTransactions(getTransactions(instrumentData));
      setTotalPvValues(getTotalPvValues());
    }
  }, [instrumentData, getTransactions, getTotalPvValues]);

  const getTableTitle = useCallback(() => {
    const exitDateObj = exitDate ? new Date(exitDate) : null;
    const yearEnding = exitDateObj ? exitDateObj.getFullYear().toString().slice(-2) : '';
    const monthEnding = exitDateObj ? exitDateObj.toLocaleString('en-GB', { month: 'short' }) : '';
    let mainTitle = '';
    switch (cashFlowType) {
      case CashFlowType.Infusion:
        mainTitle = l('_TotalPvOfInfusions');
        break;
      case CashFlowType.Other:
        mainTitle = hasInfusions
          ? l('_TotalPvOfOtherInterimCashFlows')
          : l('_TotalPvOfInterimCashFlows');
        break;
    }
    return `${mainTitle} - ${l('_YearEndingMonthYear', { monthEnding, yearEnding })}`;
  }, [exitDate, l, cashFlowType, hasInfusions]);

  useEffect(() => {
    if (exitDate) {
      setTableTitle(getTableTitle());
    }
  }, [exitDate, getTableTitle]);

  const closeModal = () => {
    setIsOpen(false);
  };

  return {
    handleCellClick,
    closeModal,
    getTransactionTypeLabel,
    isTransactionCashPaidOrDividend,
    isOpen,
    transactions,
    tableTitle,
    totalPvValues,
  };
};
